import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/layouts/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

/**
 * Reset password view.
 *
 * V9.51 hardening:
 *  - Token input is HIDDEN when the token arrives via `?token=...` in
 *    the URL (the vast majority of flows — the field only re-appears
 *    when a user hits the page manually without a link).
 *  - "Conferma password" field is mandatory; submit is disabled on
 *    mismatch, and the mismatch is echoed on the confirm field itself.
 *  - Eye toggles for BOTH password fields — improves UX for anyone
 *    typing complex passphrases on mobile.
 *  - The confirm value is also sent to the backend as
 *    `new_password_confirm`, which runs the same check server-side
 *    BEFORE the reset token gets consumed.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [tokenFromUrl, setTokenFromUrl] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    if (t) {
      setToken(t);
      setTokenFromUrl(true);
    }
  }, [params]);

  // Live mismatch signalled ONLY once the user has typed something in
  // the confirm box — avoids a jarring error on the very first keystroke.
  const mismatch = useMemo(() => {
    if (!passwordConfirm) return false;
    return password !== passwordConfirm;
  }, [password, passwordConfirm]);

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!token.trim()) return false;
    if (password.length < 6) return false;
    if (passwordConfirm.length < 6) return false;
    if (password !== passwordConfirm) return false;
    return true;
  }, [busy, token, password, passwordConfirm]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("La password deve contenere almeno 6 caratteri.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Le password non coincidono.");
      return;
    }
    setError(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
        new_password_confirm: passwordConfirm,
      });
      toast.success("Password aggiornata. Ora puoi accedere.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Imposta nuova password" subtitle="Scegli una password sicura">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
        {!tokenFromUrl && (
          <div data-testid="reset-token-field">
            <Label htmlFor="token" className="label-uppercase">Token di reset</Label>
            <Input
              id="token"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="mt-2 rounded-sm h-11"
              data-testid="reset-token-input"
            />
          </div>
        )}

        <div>
          <Label htmlFor="password" className="label-uppercase">Nuova password</Label>
          <div className="relative mt-2">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-sm h-11 pr-11"
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-500 hover:text-neutral-800"
              aria-label={showPassword ? "Nascondi password" : "Mostra password"}
              data-testid="reset-password-eye-toggle"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="password-confirm" className="label-uppercase">Conferma password</Label>
          <div className="relative mt-2">
            <Input
              id="password-confirm"
              type={showPasswordConfirm ? "text" : "password"}
              required
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className={`rounded-sm h-11 pr-11 ${mismatch ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
              data-testid="reset-password-confirm-input"
            />
            <button
              type="button"
              onClick={() => setShowPasswordConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-500 hover:text-neutral-800"
              aria-label={showPasswordConfirm ? "Nascondi password" : "Mostra password"}
              data-testid="reset-password-confirm-eye-toggle"
              tabIndex={-1}
            >
              {showPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {mismatch && (
            <p className="mt-1 text-xs text-destructive" data-testid="reset-password-mismatch">
              Le password non coincidono.
            </p>
          )}
        </div>

        {error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 p-3 rounded-sm" data-testid="reset-error">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={!canSubmit}
          className="w-full h-11 rounded-sm"
          data-testid="reset-submit-button"
        >
          {busy ? "Aggiornamento…" : "Aggiorna password"}
        </Button>
      </form>
      <div className="mt-8 text-sm text-neutral-600">
        <Link to="/login" className="text-primary hover:underline">← Torna al login</Link>
      </div>
    </AuthLayout>
  );
}
